import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Row,
  Col,
  Space,
  Card,
  notification,
  List,
  Typography,
} from "antd";
import { UserOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import "./index.css";
import DefaultLayout from "../../components/layout/DefaultLayout.js";
import Header from "../../components/header/index.js";
import lookupService from "../../services/lookup.js";
import { Excel } from "antd-table-saveas-excel";

const Lookup = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingLookup, setEditingLookup] = useState(null);
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(15);

  const showModal = () => setIsModalVisible(true);

  const handleCancel = () => {
    form.resetFields();
    setIsModalVisible(false);
    setEditingLookup(null);
  };

  const handleEdit = (record) => {
    let parsedData = [];
    try {
      parsedData =
        typeof record.data === "string" ? JSON.parse(record.data) : record.data;
    } catch (error) {
      console.error("Error parsing record.data:", error);
    }

    form.setFieldsValue({
      lookup: record.lookup,
      data: parsedData.map((item, index) => ({
        value: index + 1,
        option: item,
      })),
    });

    setEditingLookup(record);
    showModal();
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: "Confirm Deletion",
      content: `Are you sure you want to delete this lookup: ${record.lookup}?`,
      onOk: async () => {
        setLoading(true);
        try {
          const response = await lookupService.deleteLookup(record.id);
          if (response.success) {
            notification.success({
              message: "Lookup Deleted",
              description: response.message,
            });
            getLookups();
          }
        } catch (error) {
          notification.error({
            message: "Error",
            description:
              error.response?.data?.message || "Failed to delete lookup",
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleAddLookup = () => showModal();

  const getLookups = async (page = 1, perPage = 15, search = "") => {
    setLoading(true);
    try {
      const response = await lookupService.getAllLookups(page, perPage, search);
      setData(response.data);
      setTotalDataCount(response.total || response.data.length);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getLookups();
  }, []);

  const handleTableChange = (pagination) => {
    getLookups(pagination.current, itemsPerPage, searchQuery);
  };

  const handleInputChange = (event) => {
    const newSearchQuery = event.target.value;
    setSearchQuery(newSearchQuery);
    if (newSearchQuery.length > 2 || newSearchQuery === "") {
      getLookups(1, itemsPerPage, newSearchQuery);
    }
  };

  const handleExport = () => {
    const excelColumns = columns.filter((c) => c.key !== "Actions");
    const excel = new Excel();
    excel
      .addSheet("Lookups")
      .addColumns(excelColumns)
      .addDataSource(data, { str2Percent: true })
      .saveAs("Lookups.xlsx");
  };

  const onFinish = async (values) => {
    const payload = {
      lookup: values.lookup,
      data: values.data.map((item) => item.option),
    };

    try {
      setLoading(true);
      let response;

      if (editingLookup) {
        response = await lookupService.updateLookup(editingLookup.id, payload);
        notification.success({
          message: "Lookup Updated",
          description: response.message || "Lookup updated successfully",
        });
      } else {
        response = await lookupService.saveLookup(payload);
        notification.success({
          message: "Lookup Added",
          description: response.message || "Lookup added successfully",
        });
      }

      setIsModalVisible(false);
      form.resetFields();
      setEditingLookup(null);
      getLookups();
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.response?.data?.message || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Lookup",
      dataIndex: "lookup",
      key: "lookup",
      width: 150,
    },
    {
      title: "Actions",
      key: "Actions",
      width: 100,
      render: (text, record) => (
        <Space>
          <div onClick={() => handleEdit(record)} style={{ cursor: "pointer" }}>
            <EditOutlined />
          </div>
          <div
            onClick={() => handleDelete(record)}
            style={{ cursor: "pointer" }}
          >
            <DeleteOutlined />
          </div>
        </Space>
      ),
    },
  ];

  return (
    <DefaultLayout>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Header
            icon={<UserOutlined />}
            title="Lookup"
            right={
              <Button
                type="primary"
                className="btn-blue"
                onClick={handleAddLookup}
              >
                Add Lookup
              </Button>
            }
          />
        </Col>
      </Row>

      <Card>
        <Table
          style={{ minHeight: "100vh" }}
          onChange={handleTableChange}
          columns={columns}
          dataSource={data.map((item) => ({ ...item, key: item.id }))}
          rowKey={(record) => record.key}
          scroll={{ x: 600 }}
          sticky={true}
          expandable={{
            expandedRowRender: (record) => {
              let items = [];
              try {
                items =
                  typeof record.data === "string"
                    ? JSON.parse(record.data)
                    : record.data || [];
              } catch (error) {
                console.error("Error parsing expanded data:", error);
              }

              return (
                <div
                  style={{
                    marginLeft: 160, // aligns with Lookup column
                    padding: "10px 16px",
                    background: "#fafafa",
                    borderRadius: 8,
                    border: "1px solid #f0f0f0",
                  }}
                >
                  <List
                    size="small"
                    dataSource={items}
                    renderItem={(item, index) => (
                      <List.Item
                        style={{
                          borderBottom:
                            index === items.length - 1 ? "none" : "1px solid #f0f0f0",
                          padding: "6px 0",
                        }}
                      >
                        <Typography.Text style={{ fontSize: 15, color: "#333" }}>
                          {item}
                        </Typography.Text>
                      </List.Item>
                    )}
                  />
                </div>
              );
            },


            rowExpandable: (record) => !!record.data,
          }}
          pagination={{
            showSizeChanger: false,
            current: currentPage,
            total: totalDataCount,
            pageSize: itemsPerPage,
          }}
          loading={loading}
        />
      </Card>

      <Modal
        title={editingLookup ? "Edit Lookup" : "Add Lookup"}
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={900}
        style={{ top: 20 }}
      >
        <Form
          autoComplete="off"
          form={form}
          onFinish={onFinish}
          layout="vertical"
          scrollToFirstError
          className="mt-20"
        >
          <Card size="small" className="mb-10" title="Lookup Values">
            <Row gutter={[12, 12]}>
              <Col lg={12}>
                <Form.Item
                  name="lookup"
                  label="Lookup"
                  rules={[{ required: true, message: "Please enter a Lookup" }]}
                >
                  <Input size="large" placeholder="Lookup Type" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[12, 12]}>
              <Col span={24}>
                <Form.List name="data">
                  {(fields, { add, remove }) => {
                    const updateSequence = () => {
                      const values = form.getFieldValue("data") || [];
                      const updated = values.map((item, index) => ({
                        ...item,
                        value: index + 1,
                      }));
                      form.setFieldsValue({ data: updated });
                    };

                    return (
                      <>
                        {fields.map(({ key, name, fieldKey, ...restField }) => (
                          <Row key={key} align="middle" gutter={[12, 12]}>
                            <Col flex="auto">
                              <Form.Item
                                {...restField}
                                name={[name, "option"]}
                                fieldKey={[fieldKey, "option"]}
                                label="Option"
                                rules={[
                                  {
                                    required: true,
                                    message: "Please enter an option",
                                  },
                                ]}
                              >
                                <Input size="large" placeholder="Option" />
                              </Form.Item>
                            </Col>
                            <Col>
                              <Button
                                type="link"
                                onClick={() => {
                                  remove(name);
                                  setTimeout(updateSequence, 0);
                                }}
                              >
                                Remove
                              </Button>
                            </Col>
                          </Row>
                        ))}

                        <Form.Item>
                          <Button
                            type="dashed"
                            onClick={() => {
                              add({});
                              setTimeout(updateSequence, 0);
                            }}
                            block
                          >
                            Add Lookup Value
                          </Button>
                        </Form.Item>
                      </>
                    );
                  }}
                </Form.List>
              </Col>
            </Row>
          </Card>

          <Row>
            <Col span={24}>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingLookup ? "Save Changes" : "Add Lookup"}
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </DefaultLayout>
  );
};

export default Lookup;
